import { useState, useEffect, useCallback, useMemo } from "react";

const STORAGE_KEY = "maxemployee_referrals";
const USER_KEY = "maxemployee_referral_user";
const LEADERBOARD_KEY = "maxemployee_referral_leaderboard";

function generateUserId() {
  return "user_" + Math.random().toString(36).substring(2, 8);
}

function getOrCreateUserId() {
  let userId = localStorage.getItem(USER_KEY);
  if (!userId) {
    userId = generateUserId();
    localStorage.setItem(USER_KEY, userId);
  }
  return userId;
}

function loadReferrals() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveReferrals(referrals) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(referrals));
}

function loadLeaderboard() {
  try {
    const raw = localStorage.getItem(LEADERBOARD_KEY);
    if (raw) return JSON.parse(raw);
    // Generate mock leaderboard data
    const mock = [
      { id: "u1", name: "Alex Chen", avatar: null, referralCount: 47, credits: 2850 },
      { id: "u2", name: "Sarah Kim", avatar: null, referralCount: 35, credits: 2100 },
      { id: "u3", name: "Marcos Silva", avatar: null, referralCount: 28, credits: 1680 },
      { id: "u4", name: "Priya Patel", avatar: null, referralCount: 22, credits: 1320 },
      { id: "u5", name: "James Wilson", avatar: null, referralCount: 18, credits: 1080 },
      { id: "u6", name: "Emma Dubois", avatar: null, referralCount: 15, credits: 900 },
      { id: "u7", name: "Yuki Tanaka", avatar: null, referralCount: 12, credits: 720 },
      { id: "u8", name: "Carlos Rivera", avatar: null, referralCount: 9, credits: 540 },
    ];
    localStorage.setItem(LEADERBOARD_KEY, JSON.stringify(mock));
    return mock;
  } catch {
    return [];
  }
}

// Tiered reward calculation
function calcTierReward(count) {
  if (count <= 5) return 25;
  if (count <= 10) return 35;
  return 50;
}

// Milestone bonuses
function calcMilestoneBonus(totalConverted) {
  if (totalConverted >= 25) return 1000;
  if (totalConverted >= 10) return 250;
  if (totalConverted >= 5) return 100;
  return 0;
}

// Aggregate milestone bonuses earned so far
function calcTotalMilestoneBonuses(totalConverted) {
  let total = 0;
  if (totalConverted >= 5) total += 100;
  if (totalConverted >= 10) total += 250;
  if (totalConverted >= 25) total += 1000;
  return total;
}

export function useReferral() {
  const [userId] = useState(getOrCreateUserId);
  const [referrals, setReferrals] = useState([]);
  const [fraudFlags, setFraudFlags] = useState([]);

  useEffect(() => {
    const loaded = loadReferrals();
    setReferrals(loaded);

    // Check for fraud on load
    const flags = [];
    const seenEmails = new Set();
    const seenIps = new Set();
    loaded.forEach((r) => {
      if (seenEmails.has(r.email)) flags.push({ type: "duplicate_email", referralId: r.id, email: r.email });
      seenEmails.add(r.email);
      if (r.ip && seenIps.has(r.ip)) flags.push({ type: "duplicate_ip", referralId: r.id, ip: r.ip });
      if (r.ip) seenIps.add(r.ip);
      if (r.email && r.email.includes(userId)) flags.push({ type: "self_referral", referralId: r.id, email: r.email });
    });
    setFraudFlags(flags);
  }, [userId]);

  const referralLink = `https://maxemployee.com/ref/${userId}`;
  const referralCode = userId.replace("user_", "").toUpperCase();

  // Computed stats
  const stats = useMemo(() => {
    const pending = referrals.filter((r) => r.status === "pending");
    const converted = referrals.filter((r) => r.status === "converted");
    const expired = referrals.filter((r) => r.status === "expired");
    const rejected = referrals.filter((r) => r.status === "rejected");
    const approved = referrals.filter((r) => r.status === "approved");

    const totalConverted = converted.length;
    const baseCredits = converted.reduce((sum, _, i) => sum + calcTierReward(i + 1), 0);
    const milestoneBonuses = calcTotalMilestoneBonuses(totalConverted);
    const totalCredits = baseCredits + milestoneBonuses;

    const totalSent = referrals.length;
    const conversionRate = totalSent > 0 ? ((totalConverted / totalSent) * 100).toFixed(1) : "0.0";

    return {
      totalReferrals: totalSent,
      activeReferrals: pending.length,
      convertedCount: totalConverted,
      expiredCount: expired.length,
      rejectedCount: rejected.length,
      approvedCount: approved.length,
      conversionRate: parseFloat(conversionRate),
      credits: totalCredits,
      baseCredits,
      milestoneBonuses,
      pendingCount: pending.length,
    };
  }, [referrals]);

  const leaderboard = useMemo(() => {
    const lb = loadLeaderboard();
    // Insert current user
    const userEntry = {
      id: userId,
      name: "You",
      avatar: null,
      referralCount: stats.convertedCount,
      credits: stats.credits,
      isCurrentUser: true,
    };
    const all = [...lb, userEntry].sort((a, b) => b.referralCount - a.referralCount);
    return all.slice(0, 10);
  }, [stats.convertedCount, stats.credits, userId]);

  // Tier info for current user
  const tierInfo = useMemo(() => {
    const count = stats.convertedCount;
    let current, next, currentRate, nextRate, referralsToNext;
    if (count < 5) {
      current = "Bronze";
      next = "Silver";
      currentRate = 25;
      nextRate = 35;
      referralsToNext = 5 - count;
    } else if (count < 10) {
      current = "Silver";
      next = "Gold";
      currentRate = 35;
      nextRate = 50;
      referralsToNext = 10 - count;
    } else {
      current = "Gold";
      next = null;
      currentRate = 50;
      nextRate = null;
      referralsToNext = 0;
    }
    const nextMilestone = count < 5 ? 5 : count < 10 ? 10 : count < 25 ? 25 : null;
    const milestoneBonus = nextMilestone ? calcMilestoneBonus(nextMilestone) : 0;
    return { current, next, currentRate, nextRate, referralsToNext, nextMilestone, milestoneBonus, progress: nextMilestone ? ((count / nextMilestone) * 100) : 100 };
  }, [stats.convertedCount]);

  // Referral history with rewards
  const referralHistory = useMemo(() => {
    return referrals.map((r, idx) => ({
      ...r,
      baseReward: r.status === "converted" ? calcTierReward(idx + 1) : 0,
    }));
  }, [referrals]);

  // Reward history (mock historical entries + real conversions)
  const rewardHistory = useMemo(() => {
    const entries = [];
    referrals
      .filter((r) => r.status === "converted")
      .forEach((r, idx) => {
        entries.push({
          id: `reward_${r.id}`,
          type: "referral",
          amount: calcTierReward(idx + 1),
          status: "credited",
          description: `Referral bonus for ${r.email || "friend"}`,
          date: r.convertedAt || r.createdAt,
          referralId: r.id,
        });
      });
    // Add milestone bonuses at the right points
    if (stats.convertedCount >= 5) {
      entries.push({ id: "milestone_5", type: "milestone", amount: 100, status: "credited", description: "5 Referrals Milestone Bonus", date: new Date().toISOString() });
    }
    if (stats.convertedCount >= 10) {
      entries.push({ id: "milestone_10", type: "milestone", amount: 250, status: "credited", description: "10 Referrals Milestone Bonus", date: new Date().toISOString() });
    }
    if (stats.convertedCount >= 25) {
      entries.push({ id: "milestone_25", type: "milestone", amount: 1000, status: "credited", description: "25 Referrals Milestone Bonus", date: new Date().toISOString() });
    }
    entries.sort((a, b) => new Date(b.date) - new Date(a.date));
    return entries;
  }, [referrals, stats.convertedCount]);

  const sendInvite = useCallback(
    (email) => {
      const newReferral = {
        id: Date.now().toString(36) + Math.random().toString(36).substring(2, 6),
        email,
        status: "pending",
        createdAt: new Date().toISOString(),
        ip: null,
      };
      const updated = [newReferral, ...referrals];
      setReferrals(updated);
      saveReferrals(updated);
      return newReferral;
    },
    [referrals]
  );

  const markConverted = useCallback(
    (referralId) => {
      const updated = referrals.map((r) =>
        r.id === referralId ? { ...r, status: "converted", convertedAt: new Date().toISOString() } : r
      );
      setReferrals(updated);
      saveReferrals(updated);
    },
    [referrals]
  );

  const updateReferralStatus = useCallback(
    (referralId, newStatus) => {
      const updated = referrals.map((r) => (r.id === referralId ? { ...r, status: newStatus, updatedAt: new Date().toISOString() } : r));
      setReferrals(updated);
      saveReferrals(updated);
    },
    [referrals]
  );

  const bulkUpdateStatus = useCallback(
    (referralIds, newStatus) => {
      const idSet = new Set(referralIds);
      const updated = referrals.map((r) => (idSet.has(r.id) ? { ...r, status: newStatus, updatedAt: new Date().toISOString() } : r));
      setReferrals(updated);
      saveReferrals(updated);
    },
    [referrals]
  );

  const simulateConversion = useCallback(() => {
    const pending = referrals.filter((r) => r.status === "pending");
    if (pending.length > 0) {
      markConverted(pending[0].id);
    }
  }, [referrals, markConverted]);

  const copyLink = useCallback(() => {
    navigator.clipboard?.writeText(referralLink);
  }, [referralLink]);

  const shareWhatsApp = useCallback(() => {
    const text = encodeURIComponent(
      `Join Max Employee and get $25 in credits! Use my referral link: ${referralLink}`
    );
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
  }, [referralLink]);

  const shareTelegram = useCallback(() => {
    const text = encodeURIComponent(
      `Join Max Employee and get $25 in credits! Use my referral link: ${referralLink}`
    );
    window.open(
      `https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${text}`,
      "_blank",
      "noopener,noreferrer"
    );
  }, [referralLink]);

  const shareTwitter = useCallback(() => {
    const text = encodeURIComponent(
      `I'm using @MaxEmployee — the AI-powered employee management platform. Join with my link and get $25 in free credits! 🚀`
    );
    window.open(
      `https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(referralLink)}`,
      "_blank",
      "noopener,noreferrer"
    );
  }, [referralLink]);

  const shareLinkedIn = useCallback(() => {
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}`,
      "_blank",
      "noopener,noreferrer"
    );
  }, [referralLink]);

  const shareEmail = useCallback(
    (friendEmail) => {
      const subject = encodeURIComponent("Join Max Employee — Get $25 in Credits");
      const body = encodeURIComponent(
        `Hi!\n\nI've been using Max Employee for AI-powered team management and thought you'd find it valuable. Sign up with my referral link and we both get $25 in credits:\n\n${referralLink}\n\nCheers!`
      );
      window.open(`mailto:${friendEmail}?subject=${subject}&body=${body}`, "_self");
    },
    [referralLink]
  );

  const exportCSV = useCallback(() => {
    const headers = ["Email", "Status", "Created", "Converted", "Reward"];
    const rows = referrals.map((r) => [
      r.email,
      r.status,
      r.createdAt,
      r.convertedAt || "",
      r.status === "converted" ? calcTierReward(
        referrals.filter((x) => x.status === "converted").findIndex((x) => x.id === r.id) + 1
      ) : 0,
    ]);
    const csv = [headers, ...rows].map((row) => row.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `referrals_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [referrals]);

  return {
    userId,
    referralLink,
    referralCode,
    referrals,
    referralHistory,
    rewardHistory,
    stats,
    tierInfo,
    leaderboard,
    fraudFlags,
    sendInvite,
    markConverted,
    updateReferralStatus,
    bulkUpdateStatus,
    simulateConversion,
    copyLink,
    shareWhatsApp,
    shareTelegram,
    shareTwitter,
    shareLinkedIn,
    shareEmail,
    exportCSV,
  };
}
